import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton } from "@air/component-skeleton";
import { TriangleDown, TriangleRight } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import isUndefined from "lodash/isUndefined";
import pluralize from "pluralize";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useUnmount } from "__barrel_optimize__?names=useUnmount!=!react-use";
import { hasAnnotation } from "~/components/Annotations/shared/annotationUtils";
import { useDiscussionPanelContext, useDiscussionPanelContextSelector } from "~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext";
import { isDiscussionRepliesOpenSelector } from "~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextUtils";
import { CommentReply } from "~/components/Discussions/Discussion/components/CommentReply";
import { ReplyButton } from "~/components/Discussions/Discussion/components/ReplyButton";
import { TopCommentContainer } from "~/components/Discussions/Discussion/components/TopCommentContainer";
import { CommentTimestampInfo } from "~/components/Discussions/Discussion/DiscussionComment/components/CommentTimestampInfo";
import { DiscussionAnnotationInfo } from "~/components/Discussions/Discussion/DiscussionComment/components/DiscussionAnnotationInfo";
import { DiscussionComment } from "~/components/Discussions/Discussion/DiscussionComment/DiscussionComment";
import { COMMENT_AVATAR_RIGHT_MARGIN, COMMENT_AVATAR_SIZE, COMMENT_PADDING } from "~/components/Discussions/Discussion/shared/constants";
import { DISCUSSION } from "~/constants/testIDs";
import { useAccountContext } from "~/providers/AccountProvider";
import { centralizedClipVersionNumberSelector } from "~/store/centralizedClip/selectors";
import { isViewingDiscussionWithId } from "~/store/router/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
export const ClipDiscussion = /*#__PURE__*/ memo((param)=>{
    let { discussion, mentionsList, canReply, onEdit, onDelete, onToggleResolve, onSubmit, onSwitchVersion, shouldShowMentionsButton, clipId, onAnnotatedCommentClick } = param;
    const { data } = useAccountContext();
    const currentAssetVersion = useSelector(centralizedClipVersionNumberSelector);
    const isDiscussionActive = useAirSelector((st)=>isViewingDiscussionWithId(st, discussion.id));
    const { clearReplyingDiscussionId, setRepliesOpen } = useDiscussionPanelContext();
    const isReplyingCurrentDiscussionIdSelector = useCallback((state)=>state.replyingDiscussionId === discussion.id, [
        discussion.id
    ]);
    const repliesVisibleSelector = useCallback((state)=>{
        return isDiscussionRepliesOpenSelector(state, discussion.id);
    }, [
        discussion.id
    ]);
    const isAddingReply = useDiscussionPanelContextSelector(isReplyingCurrentDiscussionIdSelector);
    const areRepliesVisible = useDiscussionPanelContextSelector(repliesVisibleSelector);
    useEffect(()=>{
        if (isAddingReply) {
            setRepliesOpen(discussion.id, true);
        }
    }, [
        discussion.id,
        isAddingReply,
        setRepliesOpen
    ]);
    const commentProps = useMemo(()=>({
            currentVersion: currentAssetVersion,
            discussion,
            mentionsList,
            onDelete,
            onSave: onEdit,
            shouldShowMentionsButton,
            onToggleResolve,
            onSwitchVersion: onSwitchVersion ? ()=>onSwitchVersion(discussion.clipId) : undefined,
            showAvatar: true
        }), [
        currentAssetVersion,
        discussion,
        mentionsList,
        onDelete,
        onEdit,
        onSwitchVersion,
        onToggleResolve,
        shouldShowMentionsButton
    ]);
    const canReplyToComment = canReply && !discussion.resolved;
    const onReplySubmit = useCallback((newComment)=>{
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(newComment, discussion, clipId);
        setRepliesOpen(discussion.id, true);
    }, [
        clipId,
        discussion,
        onSubmit,
        setRepliesOpen
    ]);
    const comments = discussion.comments.data;
    const toggleReplies = useCallback(()=>{
        setRepliesOpen(discussion.id, !areRepliesVisible);
    }, [
        areRepliesVisible,
        discussion.id,
        setRepliesOpen
    ]);
    const handleCommentClick = useCallback(()=>{
        onAnnotatedCommentClick === null || onAnnotatedCommentClick === void 0 ? void 0 : onAnnotatedCommentClick({
            clipId: discussion.clipId,
            discussionId: discussion.id,
            timestamp: discussion.timestamp,
            hasAnnotation: hasAnnotation(discussion)
        });
    }, [
        discussion,
        onAnnotatedCommentClick
    ]);
    useUnmount(()=>{
        if (isAddingReply) {
            clearReplyingDiscussionId();
        }
    });
    const commentMetadataInfo = useMemo(()=>{
        var _discussion_annotation;
        const timestamp = discussion.timestamp || ((_discussion_annotation = discussion.annotation) === null || _discussion_annotation === void 0 ? void 0 : _discussion_annotation.timestamp);
        const color = isDiscussionActive ? "blue" : "grey";
        if (hasAnnotation(discussion)) {
            return /*#__PURE__*/ _jsx(DiscussionAnnotationInfo, {
                discussion: discussion,
                color: color
            });
        } else if (!isUndefined(timestamp)) {
            return /*#__PURE__*/ _jsx(CommentTimestampInfo, {
                timestamp: timestamp,
                color: color
            });
        }
        return null;
    }, [
        discussion,
        isDiscussionActive
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        "data-discussionid": discussion.id,
        "data-testid": DISCUSSION,
        children: [
            /*#__PURE__*/ _jsx(TopCommentContainer, {
                discussion: discussion,
                onClick: handleCommentClick,
                children: /*#__PURE__*/ _jsx(DiscussionComment, {
                    className: areRepliesVisible ? "bg-grey-2" : "",
                    commentMetadata: commentMetadataInfo,
                    comment: comments[0],
                    secondaryActions: /*#__PURE__*/ _jsx("div", {
                        className: "-ml-2",
                        children: comments.length === 1 ? canReplyToComment && !isAddingReply && /*#__PURE__*/ _jsx(ReplyButton, {
                            discussionId: discussion.id
                        }) : /*#__PURE__*/ _jsxs(Button, {
                            appearance: "ghost",
                            color: "grey",
                            onClick: (event)=>{
                                event.stopPropagation();
                                toggleReplies();
                            },
                            size: "small",
                            prefix: areRepliesVisible ? /*#__PURE__*/ _jsx(TriangleDown, {
                                className: "size-4"
                            }) : /*#__PURE__*/ _jsx(TriangleRight, {
                                className: "size-4"
                            }),
                            children: [
                                pluralize("Reply", comments.length - 1, true),
                                " "
                            ]
                        })
                    }),
                    ...commentProps
                })
            }),
            (areRepliesVisible || isAddingReply) && /*#__PURE__*/ _jsxs("div", {
                className: "border-t border-t-grey-5 bg-grey-2",
                children: [
                    areRepliesVisible && /*#__PURE__*/ _jsx("div", {
                        children: comments.slice(1).map((comment, index)=>/*#__PURE__*/ _jsx(DiscussionComment, {
                                className: "border-t border-t-grey-5 first:border-t-0",
                                comment: comment,
                                secondaryActions: canReplyToComment && !isAddingReply && index === comments.length - 2 && /*#__PURE__*/ _jsx(ReplyButton, {
                                    discussionId: discussion.id
                                }),
                                style: {
                                    paddingLeft: COMMENT_AVATAR_SIZE + COMMENT_AVATAR_RIGHT_MARGIN + COMMENT_PADDING
                                },
                                ...commentProps
                            }, comment.id))
                    }),
                    isAddingReply && /*#__PURE__*/ _jsx(CommentReply, {
                        user: data,
                        discussionId: discussion.id,
                        shouldShowMentionsButton: shouldShowMentionsButton,
                        mentionsList: mentionsList,
                        onSubmit: onReplySubmit
                    })
                ]
            })
        ]
    });
});
ClipDiscussion.displayName = "ClipDiscussion";
export const DiscussionSkeleton = ()=>{
    return /*#__PURE__*/ _jsxs("div", {
        className: "pt-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 mr-7 flex items-center lg:mr-11",
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        height: 24,
                        style: {
                            marginRight: 12
                        },
                        variant: "round",
                        width: 24
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        height: 10,
                        width: 60
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mr-7 pb-5 pt-2 lg:mr-11",
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        height: 10,
                        style: {
                            marginBottom: 8,
                            marginLeft: 36
                        },
                        width: "calc(90% - 36px)"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        height: 10,
                        style: {
                            marginBottom: 8,
                            marginLeft: 36
                        },
                        width: "calc(100% - 36px)"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        height: 10,
                        style: {
                            marginLeft: 36
                        },
                        width: "calc(70% - 36px)"
                    })
                ]
            })
        ]
    });
};
